import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import Pusher from 'pusher-js';
import { Channel } from 'pusher-js';
import { useAuth } from './AuthContext';

interface PusherContextType {
  pusher: Pusher | null;
  subscribe: (channelName: string) => Channel | null;
  unsubscribe: (channelName: string) => void;
  isConnected: boolean;
}

const PusherContext = createContext<PusherContextType | undefined>(undefined);

export const PusherProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pusher, setPusher] = useState<Pusher | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Don't initialize Pusher on OAuth callback pages
    const isOAuthCallback =
      window.location.pathname.includes('/auth/google/success') ||
      window.location.pathname.includes('/auth/apple/success');

    if (!user || isOAuthCallback) {
      return () => {
        if (pusher) {
          pusher.disconnect();
          setPusher(null);
          setIsConnected(false);
        }
      };
    }

    // Small delay to ensure auth token is properly set
    const initTimer = setTimeout(() => {
      // Initialize Pusher with auth endpoint
      const pusherClient = new Pusher(process.env.REACT_APP_PUSHER_KEY || '', {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'us2',
        authEndpoint: '/api/pusher/auth',
        auth: {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          },
        },
      });

      // Configure auth for private channels
      pusherClient.connection.bind('connected', () => {
        setIsConnected(true);
      });

      pusherClient.connection.bind('disconnected', () => {
        setIsConnected(false);
      });

      pusherClient.connection.bind('error', (err: any) => {
        console.error('Pusher connection error:', err);
      });

      setPusher(pusherClient);
    }, 100);

    return () => {
      clearTimeout(initTimer);
      if (pusher) {
        pusher.disconnect();
        setPusher(null);
        setIsConnected(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const subscribe = useCallback(
    (channelName: string): Channel | null => {
      if (!pusher) {
        console.log('[PusherContext] Cannot subscribe - Pusher not initialized');
        return null;
      }

      // Check if already subscribed
      const existingChannel = pusher.channel(channelName);
      if (existingChannel) {
        console.log(
          '[PusherContext] Channel already exists:',
          channelName,
          'subscribed:',
          existingChannel.subscribed,
        );
        // If channel exists but not subscribed, unsubscribe and resubscribe
        if (!existingChannel.subscribed) {
          console.log('[PusherContext] Channel not subscribed, resubscribing...');
          pusher.unsubscribe(channelName);
          return pusher.subscribe(channelName);
        }
        return existingChannel;
      }

      console.log('[PusherContext] Subscribing to new channel:', channelName);
      return pusher.subscribe(channelName);
    },
    [pusher],
  );

  const unsubscribe = useCallback(
    (channelName: string) => {
      if (!pusher) return;
      pusher.unsubscribe(channelName);
    },
    [pusher],
  );

  return (
    <PusherContext.Provider value={{ pusher, subscribe, unsubscribe, isConnected }}>
      {children}
    </PusherContext.Provider>
  );
};

export const usePusher = () => {
  const context = useContext(PusherContext);
  if (context === undefined) {
    throw new Error('usePusher must be used within a PusherProvider');
  }
  return context;
};
